import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../assets/product-list.css';

// Featured projects data
const featuredProjects = [
    {
        id: 1,
        title: "幹細胞療法研究",
        description: "探索幹細胞在再生醫學中的應用前景，助力人類健康未來。",
        imageUrl: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
        tags: ["再生醫學", "幹細胞", "創新療法"],
        demoUrl: "/product",
        status: "研究進行中"
    },
    {
        id: 2,
        title: "生命科學技術平台",
        description: "提供尖端生物技術解決方案，連接研究者與生物科技資源。",
        imageUrl: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
        tags: ["生物技術", "科研平台", "數據分析"],
        demoUrl: "/fs",
        status: "已上線"
    },
    {
        id: 3,
        title: "細胞治療推廣計劃",
        description: "促進公眾對細胞治療的認知，推動前沿醫療技術的普及應用。",
        imageUrl: "https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
        tags: ["細胞治療", "科普教育", "醫療創新"],
        demoUrl: "#",
        status: "籌備中"
    }
];

const HomeV5 = () => {
    const [scrollY, setScrollY] = useState(0);
    const heroRef = useRef(null);
    const featuredRef = useRef(null);
    const missionRef = useRef(null);
    const statsRef = useRef(null);
    const contactRef = useRef(null);
    
    // Handle scroll animation
    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
            
            // Apply animations to elements as they come into view
            const elements = [heroRef, featuredRef, missionRef, statsRef, contactRef];
            elements.forEach(ref => {
                if (ref.current) {
                    const rect = ref.current.getBoundingClientRect();
                    const isVisible = rect.top < window.innerHeight - 100;
                    if (isVisible) {
                        ref.current.classList.add('animated');
                    }
                }
            });
        };
        
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="landing-page">
            {/* Custom CSS */}
            <style jsx="true">{`
                .landing-page {
                    font-family: 'Segoe UI', 'Helvetica Neue', sans-serif;
                    color: #333;
                    overflow-x: hidden;
                }
                
                h1, h2, h3, h4, h5, h6 {
                    font-weight: 600;
                }
                
                .section {
                    padding: 100px 0;
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.6s ease, transform 0.6s ease;
                }
                
                .section.animated {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 20px;
                }
                
                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    padding: 0;
                    position: relative;
                    overflow: hidden;
                }
                
                .hero-content {
                    position: relative;
                    z-index: 2;
                    max-width: 600px;
                }
                
                .hero-title {
                    font-size: 4rem;
                    margin-bottom: 1.5rem;
                    line-height: 1.2;
                    font-weight: 800;
                    animation: fadeInUp 1s ease;
                }
                
                .hero-subtitle {
                    font-size: 1.5rem;
                    margin-bottom: 2rem;
                    line-height: 1.6;
                    font-weight: 400;
                    animation: fadeInUp 1.2s ease;
                }
                
                .hero-cta {
                    display: inline-block;
                    background: #e0e0e0;
                    color: #2d2d2d;
                    font-weight: 600;
                    font-size: 1.1rem;
                    padding: 15px 40px;
                    border-radius: 50px;
                    text-decoration: none;
                    transition: all 0.3s ease;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                    animation: fadeInUp 1.4s ease;
                }
                
                .hero-cta:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
                }
                
                .hero-particles {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    z-index: 1;
                }
                
                .particle {
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.1);
                }
                
                .stem-cell-svg {
                    position: absolute;
                    right: 5%;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 400px;
                    height: 400px;
                    z-index: 2;
                    animation: floatAnimation 6s ease-in-out infinite;
                }
                
                @keyframes floatAnimation {
                    0% { transform: translateY(-50%) translateX(0); }
                    50% { transform: translateY(-50%) translateX(-10px); }
                    100% { transform: translateY(-50%) translateX(0); }
                }
                
                /* Molecule Decorations */
                .molecule-svg {
                    position: absolute;
                    width: 150px;
                    height: 150px;
                    opacity: 0.4;
                    z-index: 1;
                }
                
                .molecule-1 {
                    top: 10%;
                    left: 10%;
                    animation: rotateSlow 20s linear infinite;
                }
                
                .molecule-2 {
                    bottom: 10%;
                    left: 15%;
                    animation: rotateSlow 25s linear infinite reverse;
                }
                
                @keyframes rotateSlow {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                /* Featured Projects */
                .featured-section {
                    background: #f4f4f4;
                }
                
                .featured-title {
                    text-align: center;
                    margin-bottom: 4rem;
                    font-size: 2.5rem;
                    color: #333;
                }
                
                .featured-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 30px;
                }
                
                .featured-card {
                    background: white;
                    border-radius: 15px;
                    overflow: hidden;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
                    transition: all 0.3s ease;
                }
                
                .featured-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 40px rgba(45, 45, 45, 0.1);
                }
                
                .featured-image {
                    position: relative;
                    height: 220px;
                    overflow: hidden;
                }
                
                .featured-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.5s ease;
                }
                
                .featured-card:hover .featured-image img {
                    transform: scale(1.05);
                }
                
                .featured-status {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: rgba(0, 0, 0, 0.75);
                    color: white;
                    padding: 8px 16px;
                    border-radius: 30px;
                    font-size: 0.8rem;
                    font-weight: 500;
                }
                
                .featured-content {
                    padding: 30px;
                }
                
                .featured-project-title {
                    font-size: 1.5rem;
                    margin-bottom: 15px;
                    color: #222;
                }
                
                .featured-description {
                    color: #666;
                    margin-bottom: 20px;
                    line-height: 1.6;
                }
                
                .featured-tags {
                    display: flex;
                    flex-wrap: wrap;
                    margin-bottom: 20px;
                    gap: 10px;
                }
                
                .featured-tag {
                    background: #e0e0e0;
                    color: #2d2d2d;
                    padding: 5px 15px;
                    border-radius: 30px;
                    font-size: 0.8rem;
                }
                
                .featured-actions {
                    display: flex;
                    justify-content: flex-end;
                }
                
                .btn-view-featured {
                    display: inline-flex;
                    align-items: center;
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    padding: 10px 20px;
                    border-radius: 30px;
                    text-decoration: none;
                    font-weight: 500;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                }
                
                .btn-view-featured i {
                    margin-left: 10px;
                }
                
                .btn-view-featured:hover {
                    transform: translateX(5px);
                }
                
                /* Mission Section */
                .mission-section {
                    background: white;
                    text-align: center;
                }
                
                .mission-title {
                    font-size: 2.5rem;
                    margin-bottom: 1.5rem;
                    color: #333;
                }
                
                .mission-subtitle {
                    font-size: 1.2rem;
                    max-width: 800px;
                    margin: 0 auto 3rem;
                    color: #666;
                    line-height: 1.7;
                }
                
                .values-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 30px;
                }
                
                .value-card {
                    background: #f9f9f9;
                    padding: 40px 30px;
                    border-radius: 15px;
                    text-align: center;
                    transition: all 0.3s ease;
                }
                
                .value-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
                }
                
                .value-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 80px;
                    height: 80px;
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    border-radius: 50%;
                    font-size: 2rem;
                    margin-bottom: 20px;
                }
                
                .value-title {
                    font-size: 1.5rem;
                    margin-bottom: 15px;
                    color: #333;
                }
                
                .value-description {
                    color: #666;
                    line-height: 1.6;
                }
                
                /* Stats Section */
                .stats-section {
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    text-align: center;
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 30px;
                }
                
                .stat-item {
                    padding: 20px;
                }
                
                .stat-number {
                    font-size: 3.5rem;
                    font-weight: 800;
                    margin-bottom: 10px;
                }
                
                .stat-label {
                    font-size: 1.2rem;
                    opacity: 0.9;
                }
                
                /* Contact Section */
                .contact-section {
                    background: #f9f9f9;
                    text-align: center;
                }
                
                .contact-title {
                    font-size: 2.5rem;
                    margin-bottom: 1.5rem;
                    color: #333;
                }
                
                .contact-subtitle {
                    font-size: 1.2rem;
                    max-width: 800px;
                    margin: 0 auto 3rem;
                    color: #666;
                    line-height: 1.7;
                }
                
                .contact-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 30px;
                }
                
                .contact-card {
                    background: white;
                    padding: 40px 30px;
                    border-radius: 15px;
                    text-align: center;
                    transition: all 0.3s ease;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
                }
                
                .contact-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 15px 40px rgba(45, 45, 45, 0.1);
                }
                
                .contact-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 70px;
                    height: 70px;
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    border-radius: 50%;
                    font-size: 1.8rem;
                    margin-bottom: 20px;
                }
                
                .contact-method {
                    font-size: 1.3rem;
                    margin-bottom: 15px;
                    color: #333;
                }
                
                .contact-detail {
                    color: #666;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
                
                .contact-link {
                    display: inline-block;
                    background: linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%);
                    color: white;
                    padding: 10px 25px;
                    border-radius: 30px;
                    text-decoration: none;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }
                
                .contact-link:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
                }
                
                /* Animations */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @media (max-width: 992px) {
                    .stem-cell-svg {
                        opacity: 0.3;
                        right: 0;
                        width: 300px;
                        height: 300px;
                    }
                    
                    .hero-content {
                        position: relative;
                        z-index: 5;
                    }
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 3rem;
                    }
                    
                    .hero-subtitle {
                        font-size: 1.3rem;
                    }
                    
                    .section {
                        padding: 70px 0;
                    }
                    
                    .featured-grid, .values-grid, .stats-grid, .contact-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .stem-cell-svg {
                        opacity: 0.2;
                        width: 200px;
                        height: 200px;
                    }
                }
            `}</style>
        
            {/* Hero Section */}
            <section ref={heroRef} className="hero-section section animated">
                <div className="hero-particles">
                    {Array.from({ length: 20 }).map((_, i) => (
                        <div
                            key={i}
                            className="particle"
                            style={{
                                width: `${Math.random() * 50 + 10}px`,
                                height: `${Math.random() * 50 + 10}px`,
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                opacity: Math.random() * 0.5 + 0.1,
                                animation: `float ${Math.random() * 10 + 5}s linear infinite`,
                                animationDelay: `${Math.random() * 5}s`
                            }}
                        />
                    ))}
                </div>
                
                {/* Stem Cell SVG */}
                <div style={{
                    position: 'absolute',
                    right: '5%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '400px',
                    height: '400px',
                    zIndex: 2,
                    animation: 'floatAnimation 6s ease-in-out infinite'
                }}>
                    <svg viewBox="0 0 400 400">
                        <defs>
                            <linearGradient id="dnaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#4a4a4a" />
                                <stop offset="100%" stopColor="#2d2d2d" />
                            </linearGradient>
                            <radialGradient id="cellGrad" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#4a4a4a" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="#2d2d2d" stopOpacity="0.4" />
                            </radialGradient>
                            <radialGradient id="nucleusGrad" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#333333" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="#1a1a1a" stopOpacity="0.6" />
                            </radialGradient>
                        </defs>
                        
                        {/* Main Cell */}
                        <circle cx="200" cy="200" r="150" fill="url(#cellGrad)" />
                        
                        {/* Nucleus */}
                        <circle cx="200" cy="200" r="60" fill="url(#nucleusGrad)" />
                        
                        {/* Organelles */}
                        <circle cx="240" cy="160" r="12" fill="#FF7F50" opacity="0.7" />
                        <circle cx="170" cy="230" r="10" fill="#77DD77" opacity="0.7" />
                        <circle cx="150" cy="150" r="8" fill="#FF7F50" opacity="0.7" />
                        <circle cx="250" cy="240" r="11" fill="#77DD77" opacity="0.7" />
                        
                        {/* Cell membrane extensions */}
                        <line x1="200" y1="50" x2="200" y2="20" stroke="#8EC5FC" strokeWidth="2" opacity="0.5" />
                        <line x1="320" y1="150" x2="350" y2="140" stroke="#8EC5FC" strokeWidth="2" opacity="0.5" />
                        <line x1="100" y1="270" x2="70" y2="290" stroke="#8EC5FC" strokeWidth="2" opacity="0.5" />
                        <line x1="280" y1="280" x2="300" y2="310" stroke="#8EC5FC" strokeWidth="2" opacity="0.5" />
                    </svg>
                </div>
                
                <div className="container">
                    <div className="hero-content">
                        <h1 className="hero-title">幹細胞研究的未來</h1>
                        <p className="hero-subtitle">
                            也許現在只能是一顆細胞，但未來會成長為一個器官，甚至是心髒成為世界的中心。
                            探索生命科學的無限可能，引領醫療科技的革新。
                        </p>
                        <a href="#featured" className="hero-cta">探索我們的研究</a>
                    </div>
                </div>
            </section>
            
            {/* Featured Projects Section */}
            <section id="featured" ref={featuredRef} className="featured-section section">
                <div className="container">
                    <h2 className="featured-title">突破性研究項目</h2>
                    <div className="featured-grid">
                        {featuredProjects.map(project => (
                            <div className="featured-card" key={project.id}>
                                <div className="featured-image">
                                    <img src={project.imageUrl} alt={project.title} />
                                    <div className="featured-status">{project.status}</div>
                                </div>
                                <div className="featured-content">
                                    <h3 className="featured-project-title">{project.title}</h3>
                                    <p className="featured-description">{project.description}</p>
                                    <div className="featured-tags">
                                        {project.tags.map((tag, index) => (
                                            <span className="featured-tag" key={index}>{tag}</span>
                                        ))}
                                    </div>
                                    <div className="featured-actions">
                                        <a 
                                            href={project.demoUrl} 
                                            className="btn-view-featured"
                                        >
                                            了解更多
                                            <i className="fa-solid fa-arrow-right"></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* Mission Section */}
            <section ref={missionRef} className="mission-section section">
                <div className="container">
                    <h2 className="mission-title">我們的使命</h2>
                    <p className="mission-subtitle">
                        致力於幹細胞研究和應用的前沿探索，推動生命科學技術創新，
                        為人類健康和醫療發展做出貢獻。
                    </p>
                    
                    {/* DNA Helix Illustration */}
                    <div style={{ textAlign: 'center', margin: '20px 0 40px' }}>
                        <svg width="100" height="160" viewBox="0 0 100 160">
                            <defs>
                                <linearGradient id="dnaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#4a4a4a" />
                                    <stop offset="100%" stopColor="#2d2d2d" />
                                </linearGradient>
                            </defs>
                            
                            {/* DNA Backbone */}
                            <path d="M30,10 C60,30 40,50 30,70 C60,90 40,110 30,130 C60,150 40,170 30,190" 
                                  stroke="url(#dnaGradient)" strokeWidth="3" fill="none" />
                            <path d="M70,10 C40,30 60,50 70,70 C40,90 60,110 70,130 C40,150 60,170 70,190" 
                                  stroke="url(#dnaGradient)" strokeWidth="3" fill="none" />
                            
                            {/* DNA Rungs */}
                            <line x1="30" y1="10" x2="70" y2="10" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="36" cy="10" r="4" fill="#FF7F50" />
                            <circle cx="64" cy="10" r="4" fill="#77DD77" />
                            
                            <line x1="40" y1="30" x2="60" y2="30" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="44" cy="30" r="4" fill="#FF7F50" />
                            <circle cx="56" cy="30" r="4" fill="#77DD77" />
                            
                            <line x1="30" y1="50" x2="70" y2="50" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="36" cy="50" r="4" fill="#FF7F50" />
                            <circle cx="64" cy="50" r="4" fill="#77DD77" />
                            
                            <line x1="40" y1="70" x2="60" y2="70" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="44" cy="70" r="4" fill="#FF7F50" />
                            <circle cx="56" cy="70" r="4" fill="#77DD77" />
                            
                            <line x1="30" y1="90" x2="70" y2="90" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="36" cy="90" r="4" fill="#FF7F50" />
                            <circle cx="64" cy="90" r="4" fill="#77DD77" />
                            
                            <line x1="40" y1="110" x2="60" y2="110" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="44" cy="110" r="4" fill="#FF7F50" />
                            <circle cx="56" cy="110" r="4" fill="#77DD77" />
                            
                            <line x1="30" y1="130" x2="70" y2="130" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="36" cy="130" r="4" fill="#FF7F50" />
                            <circle cx="64" cy="130" r="4" fill="#77DD77" />
                            
                            <line x1="40" y1="150" x2="60" y2="150" stroke="#4a4a4a" strokeWidth="2" />
                            <circle cx="44" cy="150" r="4" fill="#FF7F50" />
                            <circle cx="56" cy="150" r="4" fill="#77DD77" />
                        </svg>
                    </div>
                    
                    <div className="values-grid">
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fa-solid fa-flask"></i>
                            </div>
                            <h3 className="value-title">創新研究</h3>
                            <p className="value-description">
                                持續探索幹細胞研究的新領域，突破醫學技術的邊界，
                                開發更有效的治療方案。
                            </p>
                        </div>
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fa-solid fa-users"></i>
                            </div>
                            <h3 className="value-title">跨界合作</h3>
                            <p className="value-description">
                                促進學術界與產業界的協作，整合多學科資源和專業知識，
                                加速科研成果轉化。
                            </p>
                        </div>
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fa-solid fa-heart-pulse"></i>
                            </div>
                            <h3 className="value-title">生命關懷</h3>
                            <p className="value-description">
                                始終以人類健康福祉為核心，堅守科研倫理，
                                致力於解決重大疾病難題。
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Stats Section */}
            <section ref={statsRef} className="stats-section section">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-number">12+</div>
                            <div className="stat-label">研究項目</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">25+</div>
                            <div className="stat-label">合作夥伴</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">15+</div>
                            <div className="stat-label">專業團隊</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">3+</div>
                            <div className="stat-label">專利技術</div>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Contact Section */}
            <section ref={contactRef} className="contact-section section">
                <div className="container">
                    <h2 className="contact-title">聯絡我們</h2>
                    <p className="contact-subtitle">
                        無論您是尋求合作機會、了解研究進展，還是對幹細胞技術有任何疑問，
                        歡迎隨時與我們聯繫。
                    </p>
                    <div className="contact-grid">
                        <div className="contact-card">
                            <div className="contact-icon">
                                <i className="fa-solid fa-envelope"></i>
                            </div>
                            <h3 className="contact-method">電子郵件</h3>
                            <p className="contact-detail">
                                我們會在24小時內回覆您的郵件
                            </p>
                            <a href="mailto:contact@stemcellsoft.com" className="contact-link">
                                發送郵件
                            </a>
                        </div>
                        <div className="contact-card">
                            <div className="contact-icon">
                                <i className="fa-solid fa-phone"></i>
                            </div>
                            <h3 className="contact-method">電話諮詢</h3>
                            <p className="contact-detail">
                                工作日 9:00-18:00 提供專業諮詢服務
                            </p>
                            <a href="tel:+886123456789" className="contact-link">
                                立即撥打
                            </a>
                        </div>
                        <div className="contact-card">
                            <div className="contact-icon">
                                <i className="fa-brands fa-github"></i>
                            </div>
                            <h3 className="contact-method">技術合作</h3>
                            <p className="contact-detail">
                                查看我們的開源項目和技術文檔
                            </p>
                            <a href="https://github.com/stemcellsoft" target="_blank" rel="noopener noreferrer" className="contact-link">
                                訪問 GitHub
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomeV5;